import { MAX_HOUSEHOLD_MEMBERS, SUBSCRIPTION_PRICE_USD } from '@platewell/schema';
import { useEntitlement, useHouseholds, useIsOwner, useRecipeQuota } from '@platewell/query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ErrorCode } from '@revenuecat/purchases-js';
import { describePurchaseFailure, isBillingConfigured, reactivate, subscribe } from '../lib/billing.js';
import { usePlanPrice } from '../lib/use-plan-price.js';

/**
 * The paywall.
 *
 * Web purchases go through RevenueCat's Stripe Billing integration, with Stripe as merchant
 * of record through Managed Payments.
 * The terms below are not decoration: both app stores require a subscription's terms to be
 * accurate at the point of purchase, and the lock-on-failed-payment behaviour has to be
 * stated here as well as on the marketing site.
 */
export function Subscribe() {
  const entitlement = useEntitlement();
  const quota = useRecipeQuota();
  const isOwner = useIsOwner();
  const { active, activeHouseholdId, reloadHouseholds } = useHouseholds();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [portalOpened, setPortalOpened] = useState(false);
  // The price RevenueCat will actually charge this customer, in their own currency.
  const { formatted: price, loading: priceLoading } = usePlanPrice();

  const locked = entitlement.plan === 'locked';

  async function buy(): Promise<void> {
    if (activeHouseholdId == null) return;
    setBusy(true);
    setError(null);
    // A locked household fixes its card on the same subscription in the Stripe portal,
    // rather than buying again: Stripe is still retrying it, and a second checkout would
    // charge twice. The unlock then arrives over sync when the renewal lands.
    const outcome = locked
      ? await reactivate(activeHouseholdId)
      : await subscribe(activeHouseholdId);
    setBusy(false);

    if (!outcome.ok) {
      // Dismissing the checkout sheet is a decision, not a failure — the mobile screen has
      // always treated it that way, and an alarming banner for it is just wrong.
      if (outcome.errorCode === ErrorCode.UserCancelledError) return;
      setError(
        outcome.error === 'already_subscribed'
          ? 'This household is already subscribed. Reload to see the change.'
          : describePurchaseFailure(outcome),
      );
      return;
    }
    if (locked) {
      setPortalOpened(true);
      return;
    }
    // The entitlement arrives over sync, but the household list carries the plan too and
    // is what the switcher and billing screen read while no store is open.
    await reloadHouseholds();
    await navigate({ to: '/' });
  }

  if (entitlement.plan === 'pro' && !locked) {
    return (
      <div className="editor">
        <h1>You’re subscribed</h1>
        <p className="hint">
          {entitlement.source === 'complimentary'
            ? 'This household is included at no charge, for life. There is nothing to pay.'
            : 'All your recipes for everyone in this household.'}
        </p>
        <Link className="btn" to="/settings" search={{ tab: 'subscription' }}>
          Billing settings
        </Link>
      </div>
    );
  }

  return (
    <div className="editor">
      <h1>All your recipes for {active?.name ?? 'your household'}</h1>

      {locked ? (
        <p className="hint">
          Your payment didn’t go through, so Platewell is locked. Updating your payment
          method unlocks it as soon as the renewal goes through — nothing has been deleted.
        </p>
      ) : (
        <p className="hint">
          You’re using {quota.used} of {quota.limit ?? '∞'} free recipes. One subscription
          covers everyone in the household, on every device.
        </p>
      )}

      <section>
        <h2 className="section-title">Platewell, a year at a time</h2>
        <p>
          {priceLoading ? (
            <strong>Checking today’s price…</strong>
          ) : (
            <strong>
              {price ?? `$${SUBSCRIPTION_PRICE_USD.toFixed(2)} USD`} per year, per household.
            </strong>
          )}{' '}
          Renews automatically until you cancel.
        </p>
        <ul>
          <li>All your recipes, for every member of the household.</li>
          <li>Invite your family: up to {MAX_HOUSEHOLD_MEMBERS} people share one household.</li>
          <li>Cancel any time: you drop back to the free plan.</li>
          <li>
            If a renewal payment fails, Platewell locks three days later until it’s renewed.
            Nothing is ever deleted.
          </li>
        </ul>
      </section>

      {!isOwner && (
        <p className="hint">Only an owner of this household can subscribe.</p>
      )}

      {!locked && !isBillingConfigured() && (
        <p className="error-text">Checkout isn’t available right now. Please try again later.</p>
      )}

      <div className="row" style={{ marginTop: 14 }}>
        <button
          className="btn primary"
          // The portal is minted server-side, so a locked household needs neither the price
          // nor the Web SDK to get to it.
          disabled={busy || !isOwner || (!locked && (priceLoading || !isBillingConfigured()))}
          onClick={() => void buy()}
        >
          {locked
            ? busy
              ? 'Opening billing portal…'
              : 'Update payment method'
            : busy
              ? 'Opening checkout…'
              : priceLoading
                ? 'Checking price…'
                : price == null
                  ? 'Subscribe'
                  : `Subscribe for ${price}/year`}
        </button>
        <Link className="btn" to="/">
          Not now
        </Link>
      </div>

      {portalOpened && (
        <p className="hint">
          The billing portal opened in a new tab. Platewell unlocks here once the payment goes
          through.
        </p>
      )}

      {error != null && <p className="error-text">{error}</p>}
    </div>
  );
}
